import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import DropZone from "../../../components/DropZone";
import { adalApiFetch } from "../../../../adalConfig";
import FormModal from "../../../components/FormModal";
import DatePicker from "../../../shared/DatePicker";
import { getAPIDataParamsReturn, postApiJson, putApiJson } from "../../../utils/apiFunction";
import { textfieldDateFormat } from "../../../utils/dateFunctions";

const createConditionReportForm = (report) => ({
    temperature: report?.temperature?.toString() ?? "",
    salinity: report?.salinity?.toString() ?? "",
    ph: report?.ph?.toString() ?? "",
    gutContentPercent: report?.gutContentPercent?.toString() ?? "",
    length: report?.length?.toString() ?? "",
    density: report?.density?.toString() ?? "",
    oilDropletsCondition: report?.oilDropletsCondition ?? "",
    notes: report?.notes ?? ""
});

const parseConditionReportNumber = (value) => {
    if (typeof value !== "string" || value.trim() === "") {
        return null;
    }

    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue : null;
};

const formatConditionReportValue = (value) => {
    if (value === null || value === undefined || value === "") {
        return "-";
    }

    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue.toString() : "-";
};

const formatConditionReportDate = (value) => {
    if (!value) {
        return "-";
    }

    const parsedDate = new Date(value);
    return Number.isNaN(parsedDate.getTime())
        ? "-"
        : new Intl.DateTimeFormat("en-AU", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        }).format(parsedDate);
};

const formatGroupImageDate = (value) => formatConditionReportDate(value);

const HatcheryConditionReportModal = ({ open, handleClose, groupIds, groups, onSaved }) => {
    const normalisedGroupIds = React.useMemo(() => {
        return Array.isArray(groupIds) ? groupIds.filter((groupId) => !!groupId) : [];
    }, [groupIds]);
    const selectedGroupId = normalisedGroupIds.length === 1 ? normalisedGroupIds[0] : null;

    const [conditionReports, setConditionReports] = React.useState([]);
    const [loadingConditionReports, setLoadingConditionReports] = React.useState(false);
    const [selectedConditionReportId, setSelectedConditionReportId] = React.useState(null);
    const [conditionReportForm, setConditionReportForm] = React.useState(createConditionReportForm);
    const [conditionReportDate, setConditionReportDate] = React.useState(new Date());
    const [conditionReportError, setConditionReportError] = React.useState("");
    const [savingConditionReport, setSavingConditionReport] = React.useState(false);
    const [groupImages, setGroupImages] = React.useState([]);
    const [loadingGroupImages, setLoadingGroupImages] = React.useState(false);
    const [files, setFiles] = React.useState([]);
    const [savingGroupImage, setSavingGroupImage] = React.useState(false);
    const [groupImageError, setGroupImageError] = React.useState("");
    const uri = process.env.REACT_APP_BASE_URL;

    const resetConditionReportEditor = React.useCallback(() => {
        setSelectedConditionReportId(null);
        setConditionReportForm(createConditionReportForm());
        setConditionReportDate(new Date());
        setConditionReportError("");
    }, []);

    const loadConditionReportHistory = React.useCallback(async (requestedGroupIds) => {
        const targetGroupIds = requestedGroupIds ?? normalisedGroupIds;

        if (!targetGroupIds || targetGroupIds.length === 0) {
            setConditionReports([]);
            return;
        }

        setLoadingConditionReports(true);
        try {
            const data = await getAPIDataParamsReturn("/Farms/Groups/ConditionReports", { GroupIds: targetGroupIds });
            setConditionReports(data || []);
        } catch (error) {
            console.error(error);
            setConditionReports([]);
            setConditionReportError("Unable to load historical condition reports.");
        } finally {
            setLoadingConditionReports(false);
        }
    }, [normalisedGroupIds]);

    const loadGroupImages = React.useCallback(async (groupId) => {
        if (!groupId) {
            setGroupImages([]);
            setFiles([]);
            setGroupImageError("");
            return;
        }

        setLoadingGroupImages(true);
        setGroupImageError("");
        try {
            const data = await getAPIDataParamsReturn("/Farms/GroupImages/Get", { GroupId: groupId });
            setGroupImages(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error(error);
            setGroupImages([]);
            setGroupImageError("Unable to load images for this group.");
        } finally {
            setLoadingGroupImages(false);
        }
    }, []);

    React.useEffect(() => {
        if (!open) {
            setConditionReports([]);
            setLoadingConditionReports(false);
            setSavingConditionReport(false);
            setGroupImages([]);
            setLoadingGroupImages(false);
            setFiles([]);
            setSavingGroupImage(false);
            setGroupImageError("");
            resetConditionReportEditor();
            return;
        }

        resetConditionReportEditor();
        void loadConditionReportHistory(normalisedGroupIds);
        void loadGroupImages(selectedGroupId);
    }, [open, normalisedGroupIds, selectedGroupId, loadConditionReportHistory, loadGroupImages, resetConditionReportEditor]);

    const getConditionReportGroupLabel = (groupId) => {
        const group = (groups || []).find((item) => item.id === groupId);
        if (!group) {
            return `Group ${groupId}`;
        }

        const cropLabel = group.cropLabel || `Group ${groupId}`;
        const gradeLabel = group.grade?.description ? ` • ${group.grade.description}` : "";
        return `${cropLabel}${gradeLabel}`;
    };

    const startEditingConditionReport = (report) => {
        setSelectedConditionReportId(report.id);
        setConditionReportForm(createConditionReportForm(report));
        setConditionReportDate(report?.created ? new Date(report.created) : new Date());
        setConditionReportError("");
    };

    const saveGroupImage = async () => {
        if (!selectedGroupId) {
            setGroupImageError("Select exactly one group to add an image.");
            return;
        }

        if (!files[0]) {
            setGroupImageError("Choose an image first.");
            return;
        }

        setSavingGroupImage(true);
        setGroupImageError("");

        try {
            const formData = new FormData();
            formData.append("uploadfile", files[0], files[0].name || "uploadfile");

            const response = await adalApiFetch(fetch, `${uri}/Farms/GroupImages/Add?GroupId=${selectedGroupId}`, {
                method: "POST",
                body: formData,
                redirect: "follow"
            });

            if (response.ok) {
                setFiles([]);
                await loadGroupImages(selectedGroupId);
            } else {
                const message = await response.text();
                setGroupImageError(message || "Unable to save the image.");
            }
        } catch (error) {
            console.error(error);
            setGroupImageError("Unable to save the image.");
        } finally {
            setSavingGroupImage(false);
        }
    };

    const saveConditionReport = async () => {
        const temperature = parseConditionReportNumber(conditionReportForm.temperature);
        const salinity = parseConditionReportNumber(conditionReportForm.salinity);
        const ph = parseConditionReportNumber(conditionReportForm.ph);
        const gutContentPercent = parseConditionReportNumber(conditionReportForm.gutContentPercent);
        const length = parseConditionReportNumber(conditionReportForm.length);
        const density = parseConditionReportNumber(conditionReportForm.density);
        const oilDropletsCondition = conditionReportForm.oilDropletsCondition.trim();
        const notes = conditionReportForm.notes.trim();

        if (normalisedGroupIds.length === 0) {
            setConditionReportError("Select at least one occupied group before saving a condition report.");
            return;
        }

        if (temperature === null && salinity === null && ph === null && gutContentPercent === null && length === null && density === null && oilDropletsCondition === "" && notes === "") {
            setConditionReportError("Enter at least one condition value or notes before saving.");
            return;
        }

        setSavingConditionReport(true);
        setConditionReportError("");

        try {
            const payload = {
                Temperature: temperature,
                Salinity: salinity,
                PH: ph,
                GutContentPercent: gutContentPercent,
                Length: length,
                Density: density,
                OilDropletsCondition: oilDropletsCondition || null,
                Date: textfieldDateFormat(conditionReportDate),
                Notes: notes || null
            };
            const response = selectedConditionReportId
                ? await putApiJson(`/Farms/Groups/ConditionReports/${selectedConditionReportId}`, payload)
                : await postApiJson("/Farms/Groups/ConditionReports/Create", {
                    GroupIds: normalisedGroupIds,
                    ...payload
                });

            if (response.ok) {
                resetConditionReportEditor();
                await loadConditionReportHistory(normalisedGroupIds);
                if (typeof onSaved === "function") {
                    onSaved();
                }
            } else {
                const message = await response.text();
                setConditionReportError(message || "Unable to save the condition report.");
            }
        } catch (error) {
            console.error(error);
            setConditionReportError("Unable to save the condition report.");
        } finally {
            setSavingConditionReport(false);
        }
    };

    return (
        <FormModal open={open} handleClose={handleClose} title={"Record Condition Report"}>
            <Box sx={{ mt: 1, minWidth: { xs: 260, sm: 420 }, maxWidth: 820 }}>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    {selectedConditionReportId
                        ? "Editing a historical report. Saving now updates only the selected report entry."
                        : `Saving a new report to ${normalisedGroupIds.length} selected group${normalisedGroupIds.length === 1 ? "" : "s"}.`}
                </Typography>
                <Box sx={{ mb: 2 }}>
                    <DatePicker date={conditionReportDate} setDate={setConditionReportDate} label="Report Date" />
                </Box>
                <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        Water
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField
                                fullWidth
                                label="Temperature"
                                type="number"
                                value={conditionReportForm.temperature}
                                onChange={(event) => setConditionReportForm((previous) => ({ ...previous, temperature: event.target.value }))}
                                inputProps={{ step: "0.01" }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField
                                fullWidth
                                label="Salinity"
                                type="number"
                                value={conditionReportForm.salinity}
                                onChange={(event) => setConditionReportForm((previous) => ({ ...previous, salinity: event.target.value }))}
                                inputProps={{ min: 0, step: "0.01" }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField
                                fullWidth
                                label="pH"
                                type="number"
                                value={conditionReportForm.ph}
                                onChange={(event) => setConditionReportForm((previous) => ({ ...previous, ph: event.target.value }))}
                                inputProps={{ step: "0.01" }}
                            />
                        </Grid>
                    </Grid>
                </Box>

                <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        Condition
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField
                                fullWidth
                                label="Gut Content %"
                                type="number"
                                value={conditionReportForm.gutContentPercent}
                                onChange={(event) => setConditionReportForm((previous) => ({ ...previous, gutContentPercent: event.target.value }))}
                                inputProps={{ min: 0, step: "0.01" }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField
                                fullWidth
                                label="Length (um)"
                                type="number"
                                value={conditionReportForm.length}
                                onChange={(event) => setConditionReportForm((previous) => ({ ...previous, length: event.target.value }))}
                                inputProps={{ min: 0, step: "0.01" }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField
                                fullWidth
                                label="Density"
                                type="number"
                                value={conditionReportForm.density}
                                onChange={(event) => setConditionReportForm((previous) => ({ ...previous, density: event.target.value }))}
                                inputProps={{ min: 0, step: "0.01" }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Oil Droplets Condition"
                                value={conditionReportForm.oilDropletsCondition}
                                onChange={(event) => setConditionReportForm((previous) => ({ ...previous, oilDropletsCondition: event.target.value }))}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Notes"
                                multiline
                                minRows={4}
                                value={conditionReportForm.notes}
                                onChange={(event) => setConditionReportForm((previous) => ({ ...previous, notes: event.target.value }))}
                            />
                        </Grid>
                    </Grid>
                </Box>
                {conditionReportError ? (
                    <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                        {conditionReportError}
                    </Typography>
                ) : null}
                <Stack direction="row" spacing={1} sx={{ mt: 2, justifyContent: "flex-end" }}>
                    {selectedConditionReportId ? (
                        <Button variant="outlined" onClick={resetConditionReportEditor} disabled={savingConditionReport}>
                            New Report
                        </Button>
                    ) : null}
                    <Button variant="outlined" onClick={handleClose} disabled={savingConditionReport}>
                        Close
                    </Button>
                    <Button variant="contained" onClick={saveConditionReport} disabled={savingConditionReport}>
                        {selectedConditionReportId ? "Update Report" : "Save New Report"}
                    </Button>
                </Stack>

                <Box sx={{ mt: 3, pt: 2, borderTop: "1px solid #d9d9d9" }}>
                    <Stack direction="row" spacing={1} sx={{ justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Group Images
                        </Typography>
                        <Button
                            size="small"
                            variant="text"
                            onClick={() => loadGroupImages(selectedGroupId)}
                            disabled={loadingGroupImages || !selectedGroupId}
                        >
                            Refresh
                        </Button>
                    </Stack>

                    {!selectedGroupId ? (
                        <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
                            Select exactly one group to add or view images.
                        </Typography>
                    ) : (
                        <Stack spacing={2} sx={{ mt: 1 }}>
                            <DropZone files={files} setFiles={setFiles} />
                            <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end" }}>
                                <Button variant="contained" onClick={saveGroupImage} disabled={savingGroupImage || files.length === 0}>
                                    {savingGroupImage ? "Uploading..." : "Add Image"}
                                </Button>
                            </Stack>

                            {groupImageError ? (
                                <Typography variant="body2" color="error">
                                    {groupImageError}
                                </Typography>
                            ) : null}

                            {loadingGroupImages ? (
                                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                                    <CircularProgress size={24} />
                                </Box>
                            ) : groupImages.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                    No images have been added to this group yet.
                                </Typography>
                            ) : (
                                <Stack direction="row" spacing={1.5} sx={{ flexWrap: "wrap" }}>
                                    {groupImages.map((image) => {
                                        const imageUrl = image.imageURL || image.imageUrl;
                                        return (
                                            <Box key={image.id} sx={{ width: 140 }}>
                                                <Box
                                                    component="a"
                                                    href={imageUrl}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    sx={{ textDecoration: "none", color: "inherit" }}
                                                >
                                                    <Box
                                                        component="img"
                                                        src={imageUrl}
                                                        alt={`Group ${selectedGroupId}`}
                                                        sx={{ width: "100%", height: 100, objectFit: "cover", borderRadius: 1, border: "1px solid #d0d0d0" }}
                                                    />
                                                    <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
                                                        {formatGroupImageDate(image.date)}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </Stack>
                            )}
                        </Stack>
                    )}
                </Box>

                <Box sx={{ mt: 3, pt: 2, borderTop: "1px solid #d9d9d9" }}>
                    <Stack direction="row" spacing={1} sx={{ justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Historical Reports
                        </Typography>
                        <Button
                            size="small"
                            variant="text"
                            onClick={() => loadConditionReportHistory(normalisedGroupIds)}
                            disabled={loadingConditionReports || normalisedGroupIds.length === 0}
                        >
                            Refresh
                        </Button>
                    </Stack>
                    <Typography variant="body2" sx={{ mt: 0.5, color: "text.secondary" }}>
                        Reports are shown newest first by report date.
                    </Typography>

                    {loadingConditionReports ? (
                        <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : conditionReports.length === 0 ? (
                        <Typography variant="body2" sx={{ mt: 2 }}>
                            No previous condition reports for the selected groups.
                        </Typography>
                    ) : (
                        <Stack spacing={1.5} sx={{ mt: 2, maxHeight: 320, overflowY: "auto", pr: 0.5 }}>
                            {conditionReports.map((report) => {
                                const isSelected = report.id === selectedConditionReportId;

                                return (
                                    <Box
                                        key={report.id}
                                        sx={{
                                            p: 1.5,
                                            border: isSelected ? "2px solid #1976d2" : "1px solid #cfcfcf",
                                            backgroundColor: isSelected ? "rgba(25,118,210,0.08)" : "#fafafa",
                                            borderRadius: 1
                                        }}
                                    >
                                        <Stack direction="row" spacing={1} sx={{ justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap" }}>
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                    Date: {formatConditionReportDate(report.created)}
                                                </Typography>
                                                <Typography variant="body2" sx={{ mt: 0.25 }}>
                                                    {getConditionReportGroupLabel(report.groupId)}
                                                </Typography>
                                            </Box>
                                            <Button
                                                size="small"
                                                variant={isSelected ? "contained" : "outlined"}
                                                onClick={() => startEditingConditionReport(report)}
                                            >
                                                {isSelected ? "Editing" : "Edit"}
                                            </Button>
                                        </Stack>

                                        <Box sx={{ mt: 1 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                Water
                                            </Typography>
                                            <Typography variant="body2" sx={{ mt: 0.25 }}>
                                                Temperature: {formatConditionReportValue(report.temperature)}
                                            </Typography>
                                            <Typography variant="body2" sx={{ mt: 0.25 }}>
                                                Salinity: {formatConditionReportValue(report.salinity)}
                                            </Typography>
                                            <Typography variant="body2" sx={{ mt: 0.25 }}>
                                                pH: {formatConditionReportValue(report.ph)}
                                            </Typography>
                                        </Box>

                                        <Box sx={{ mt: 1 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                Condition
                                            </Typography>
                                            <Typography variant="body2" sx={{ mt: 0.25 }}>
                                                Gut Content %: {formatConditionReportValue(report.gutContentPercent)}
                                            </Typography>
                                            <Typography variant="body2" sx={{ mt: 0.25 }}>
                                                Length: {formatConditionReportValue(report.length)}
                                            </Typography>
                                            <Typography variant="body2" sx={{ mt: 0.25 }}>
                                                Density: {formatConditionReportValue(report.density)}
                                            </Typography>
                                            <Typography variant="body2" sx={{ mt: 0.25 }}>
                                                Oil Droplets Condition: {report.oilDropletsCondition || "-"}
                                            </Typography>
                                            <Typography variant="body2" sx={{ mt: 0.25 }}>
                                                Notes: {report.notes || "-"}
                                            </Typography>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Stack>
                    )}
                </Box>
            </Box>
        </FormModal>
    );
};

export default HatcheryConditionReportModal;